import React, { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster, toast } from "@/components/ui/sonner";
import { ErrorBoundary, installGlobalErrorReporters } from "@/utils/errorReporter";
import { blurActiveInput } from "@/lib/keyboard";
import AnalyticsConsentBanner from "@/components/AnalyticsConsentBanner";
import { captureAttribution } from "@/utils/attribution";
import { trackEvent } from "@/utils/analytics";
import { logEvent } from "@/lib/analytics";
import { resolveOnboardingGate } from "@/utils/resumeJourney";

installGlobalErrorReporters();

// Landing stays eager — it's the marketing/ad entry point and must paint instantly.
import Landing from "@/pages/Landing";

// Everything else is code-split (React.lazy) so the initial bundle stays tiny.
// Each route downloads its own JS chunk only when first visited.
const Auth = lazy(() => import("@/pages/Auth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ResumeEditor = lazy(() => import("@/pages/ResumeEditor"));
const CoverLetterGenerator = lazy(() => import("@/pages/CoverLetterGenerator"));
const JobTracker = lazy(() => import("@/pages/JobTracker"));
const JobsHub = lazy(() => import("@/pages/JobsHub"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminEconomics = lazy(() => import("@/pages/AdminEconomics"));
const AdminPaymentHealth = lazy(() => import("@/pages/AdminPaymentHealth"));
const AdminResumes = lazy(() => import("@/pages/AdminResumes"));
const Settings = lazy(() => import("@/pages/Settings"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const InterviewPrep = lazy(() => import("@/pages/InterviewPrep"));
const ImmigrationChecklist = lazy(() => import("@/pages/ImmigrationChecklist"));
const CountryKeywords = lazy(() => import("@/pages/CountryKeywords"));
const CoachDashboard = lazy(() => import("@/pages/CoachDashboard"));
const ClientResumeEditor = lazy(() => import("@/pages/ClientResumeEditor"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const SalaryInsights = lazy(() => import("@/pages/SalaryInsights"));
const ActionVerbs = lazy(() => import("@/pages/ActionVerbs"));
const Scanner = lazy(() => import("@/pages/Scanner"));
const CvBulletFix = lazy(() => import("@/pages/CvBulletFix"));
const JobDescriptionMatch = lazy(() => import("@/pages/JobDescriptionMatch"));
const LinkedInChecklist = lazy(() => import("@/pages/LinkedInChecklist"));
const EmailTemplates = lazy(() => import("@/pages/EmailTemplates"));
const SalaryNegotiation = lazy(() => import("@/pages/SalaryNegotiation"));
const BuyCredits = lazy(() => import("@/pages/BuyCredits"));
const Support = lazy(() => import("@/pages/Support"));
const JobSites = lazy(() => import("@/pages/JobSites"));
const PaymentCallback = lazy(() => import("@/pages/PaymentCallback"));
const FirstApplicationPack = lazy(() => import("@/pages/FirstApplicationPack"));
const GlobalSearchHub = lazy(() => import("@/pages/GlobalSearchHub"));
const About = lazy(() => import("@/pages/About"));
const Guides = lazy(() => import("@/pages/Guides"));
const QuickResumeWizard = lazy(() => import("@/pages/QuickResumeWizard"));
const MagicResume = lazy(() => import("@/pages/MagicResume"));
const JobFitAnalyzer = lazy(() => import("@/pages/JobFitAnalyzer"));
const JobTailor = lazy(() => import("@/pages/JobTailor"));
const MockInterview = lazy(() => import("@/pages/MockInterview"));
const InterviewHistory = lazy(() => import("@/pages/InterviewHistory"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const PublicResume = lazy(() => import("@/pages/PublicResume"));
const JapaSchools = lazy(() => import("@/pages/JapaSchools"));
const VerifyEmailRequired = lazy(() => import("@/pages/VerifyEmailRequired"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/blog/BlogPost"));

// Full-screen spinner shown while a lazy route chunk downloads.
const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center" data-testid="route-loading-fallback">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Hard-gate for admin-only routes. Renders nothing until /api/usage resolves.
// Unlike the try/catch pattern, this can't be bypassed by blocking network calls
// in dev console — the component will stay in loading state forever rather than
// render the admin UI skeleton.
const AdminRoute = ({ children }) => {
  const { user, loading, api } = useAuth();
  const [usage, setUsage] = useState(null);
  const [checkFailed, setCheckFailed] = useState(false);

  useEffect(() => {
    if (!user || loading) return;
    api.get('/usage')
      .then(r => setUsage(r.data))
      .catch(() => setCheckFailed(true));
  }, [user, loading, api]);

  if (loading || (user && !usage && !checkFailed)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (checkFailed || !usage?.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Paths that are allowed WITHOUT completing onboarding / subscribing.
// Everything else triggers the hard paywall → /pricing?first=true
const ONBOARDING_ALLOWED_PATHS = [
  "/pricing",
  "/payment/callback",
  "/settings",
  "/support",
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/privacy",
  "/terms",
  "/about",
  "/job-fit",
  "/verify-email",
  "/verify-email-required",
];

// Paths an UNVERIFIED authenticated user may visit. Everything else redirects to
// /verify-email-required. Keep this tight — security-first.
const UNVERIFIED_ALLOWED_PATHS = [
  "/verify-email",
  "/verify-email-required",
  "/settings",
  "/support",
  "/privacy",
  "/terms",
  "/about",
  "/auth",
  "/forgot-password",
  "/reset-password",
];

// Protected Route Component — enforces email verification first, then the hard paywall.
const ProtectedRoute = ({ children }) => {
  const { user, loading, api } = useAuth();
  const location = useLocation();
  const [usageChecked, setUsageChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsVerify, setNeedsVerify] = useState(false);

  useEffect(() => {
    if (!user || loading) return;
    let cancelled = false;
    api.get('/usage')
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        // Security-first: if user hasn't verified email yet, gate the whole app
        setNeedsVerify(!d.email_verified);
        // Paywall: brand-new verified user with no active paid subscription AND has not explicitly skipped onboarding
        const gate = !d.has_active_subscription && !d.onboarding_completed;
        setNeedsOnboarding(gate);
      })
      .catch(() => { if (!cancelled) { setNeedsOnboarding(false); setNeedsVerify(false); } })
      .finally(() => { if (!cancelled) setUsageChecked(true); });
    return () => { cancelled = true; };
  }, [user, loading, api]);

  if (loading || (user && !usageChecked)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Hard email-verification gate (defense-in-depth — backend also blocks sensitive ops).
  const gate = resolveOnboardingGate({
    needsVerify, needsOnboarding, path: location.pathname,
    verifyAllowed: UNVERIFIED_ALLOWED_PATHS.some(p => location.pathname.startsWith(p)),
    onboardingAllowed: ONBOARDING_ALLOWED_PATHS.some(p => location.pathname.startsWith(p)),
  });
  if (gate === 'verify') {
    return <Navigate to="/verify-email-required" replace />;
  }

  // Hard paywall: only exempt specific safe paths (pricing, payment callback, settings…)
  if (gate === 'pricing') {
    return <Navigate to="/pricing?first=true" replace />;
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/r/:token" element={<PublicResume />} />
      <Route path="/auth" element={<PublicRoute><Auth /></PublicRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/first-application-pack" element={<ProtectedRoute><FirstApplicationPack /></ProtectedRoute>} />
      <Route path="/global-search" element={<ProtectedRoute><GlobalSearchHub /></ProtectedRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/resume/new" element={<ProtectedRoute><ResumeEditor /></ProtectedRoute>} />
      <Route path="/resume/quick" element={<ProtectedRoute><QuickResumeWizard /></ProtectedRoute>} />
      <Route path="/resume/magic" element={<ProtectedRoute><MagicResume /></ProtectedRoute>} />
      <Route path="/job-fit" element={<ProtectedRoute><JobFitAnalyzer /></ProtectedRoute>} />
      <Route path="/job-tailor" element={<ProtectedRoute><JobTailor /></ProtectedRoute>} />
      <Route path="/mock-interview" element={<ProtectedRoute><MockInterview /></ProtectedRoute>} />
      <Route path="/mock-interview/history" element={<ProtectedRoute><InterviewHistory /></ProtectedRoute>} />
      {/* Email verification routes */}
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/verify-email-required" element={<ProtectedRoute><VerifyEmailRequired /></ProtectedRoute>} />
      {/* Legacy redirects — ATS Checker + Job Matcher merged into Job Fit Analyzer */}
      <Route path="/job-matcher" element={<Navigate to="/job-fit" replace />} />
      <Route path="/resume/:id" element={<ProtectedRoute><ResumeEditor /></ProtectedRoute>} />
      <Route path="/cover-letter" element={<ProtectedRoute><CoverLetterGenerator /></ProtectedRoute>} />
      <Route path="/applications" element={<ProtectedRoute><JobTracker /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobsHub /></ProtectedRoute>} />
      <Route path="/interview-prep" element={<ProtectedRoute><InterviewPrep /></ProtectedRoute>} />
      <Route path="/immigration" element={<ProtectedRoute><ImmigrationChecklist /></ProtectedRoute>} />
      <Route path="/japa-schools" element={<ProtectedRoute><JapaSchools /></ProtectedRoute>} />
      <Route path="/country-keywords" element={<ProtectedRoute><CountryKeywords /></ProtectedRoute>} />
      <Route path="/devon" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/devon/economics" element={<AdminRoute><AdminEconomics /></AdminRoute>} />
      <Route path="/devon/payment-health" element={<AdminRoute><AdminPaymentHealth /></AdminRoute>} />
      <Route path="/devon/resumes" element={<AdminRoute><AdminResumes /></AdminRoute>} />
      <Route path="/coach" element={<ProtectedRoute><CoachDashboard /></ProtectedRoute>} />
      <Route path="/client-resume/new" element={<ProtectedRoute><ClientResumeEditor /></ProtectedRoute>} />
      <Route path="/client-resume/:id" element={<ProtectedRoute><ClientResumeEditor /></ProtectedRoute>} />
      <Route path="/referrals" element={<ProtectedRoute><Referrals /></ProtectedRoute>} />
      <Route path="/salary-insights" element={<SalaryInsights />} />
      <Route path="/action-verbs" element={<ActionVerbs />} />
      <Route path="/scanner" element={<Scanner />} />
      <Route path="/cv-bullet-fix" element={<CvBulletFix />} />
      <Route path="/job-description-match" element={<JobDescriptionMatch />} />
      <Route path="/ats-checker" element={<Scanner />} />
      <Route path="/linkedin-checklist" element={<ProtectedRoute><LinkedInChecklist /></ProtectedRoute>} />
      <Route path="/email-templates" element={<ProtectedRoute><EmailTemplates /></ProtectedRoute>} />
      <Route path="/salary-negotiation" element={<ProtectedRoute><SalaryNegotiation /></ProtectedRoute>} />
      <Route path="/buy-credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/job-sites" element={<JobSites />} />
      <Route path="/about" element={<About />} />
      <Route path="/guides" element={<Guides />} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <div className="App min-h-screen bg-background">
              <RouteEffects />
              <AppRoutes />
              <UpgradeSoftGate />
              <AnalyticsConsentBanner />
              <Toaster position="top-right" richColors />
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

// Global route-level effects (must live inside <BrowserRouter>):
//  1. Scroll every newly-opened page to the TOP (fixes pages opening scrolled to the bottom).
//  2. Tap anywhere outside a form field dismisses the mobile keyboard (native-feeling UX).
function RouteEffects() {
  const { pathname, search } = useLocation();
  const { user } = useAuth();
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;
    const page = pathname.startsWith('/resume/') && !['/resume/new', '/resume/magic'].includes(pathname)
      ? '/resume/:id' : pathname;
    logEvent('page_viewed', { page }, { external: false });
  }, [pathname, userId]);

  useEffect(() => {
    // Stop the browser from restoring the previous page's scroll position.
    try {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    } catch { /* Safari strict privacy can reject History API writes. */ }
  }, []);

  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* non-fatal browser restriction */ }
    try { document.documentElement.scrollTop = 0; } catch { /* non-fatal */ }
    try { if (document.body) document.body.scrollTop = 0; } catch { /* non-fatal */ }

    if (
      window.__resumeReadyAnalyticsEnabled &&
      window.__resumeReadyLastPageViewPath !== pathname
    ) {
      trackEvent(pathname === '/' ? 'landing_view' : 'page_view', { page_path: pathname });
      window.__resumeReadyLastPageViewPath = pathname;
    }

  }, [pathname]);

  useEffect(() => {
    captureAttribution(search);
  }, [search]);

  useEffect(() => {
    const onPointerDown = (e) => {
      const t = e.target;
      if (!t) return;
      const isFieldOrControl =
        t.tagName === 'INPUT' ||
        t.tagName === 'TEXTAREA' ||
        t.tagName === 'SELECT' ||
        t.isContentEditable ||
        (typeof t.closest === 'function' &&
          t.closest('input, textarea, select, label, [contenteditable="true"]'));
      if (!isFieldOrControl) blurActiveInput();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return null;
}

// Soft-gate: when a 403-upgrade event fires, show a NON-blocking toast with an
// Feature-specific upgrade messaging for the global soft-gate toast
const UPGRADE_MESSAGES = {
  job_tailor: {
    title: 'Job Tailor needs Starter plan (₦5,000/mo)',
    description: 'AI rewrites your bullets around the job description’s skills and keywords — then you review every change.',
  },
  job_fit: {
    title: 'Job Fit Analyzer — unlock on Starter (₦5,000/mo)',
    description: 'See your exact ATS match score + every missing keyword before you apply.',
  },
  ats_checker: {
    title: 'ATS Score needs Starter plan (₦5,000/mo)',
    description: 'Know your ATS match score before submitting — stop getting auto-rejected.',
  },
  mock_interview: {
    title: 'Mock Interview — unlock on Starter (₦5,000/mo)',
    description: 'Practise five AI questions tailored to your resume and get clear feedback for your next interview.',
  },
  cover_letters: {
    title: 'Cover Letter templates — Starter plan (₦5,000/mo)',
    description: 'Create a tailored cover letter with a strong opening and relevant job-description keywords.',
  },
  ai_credits: {
    title: 'Out of AI credits — upgrade to refill',
    description: 'Starter gets 10 credits/mo · Professional gets 50. Paid credits carry forward while your paid plan remains active. Launch pricing ends soon.',
  },
};

// Global soft-gate: converts 403 "upgrade-required" events into
// actionable toast messages instead of a hard modal blocker.
function UpgradeSoftGate() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      const feature = e?.detail?.feature || '';
      const requiredPlan = (e?.detail?.requiredPlan || 'Premium')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      const featureName = e?.detail?.featureName || 'This feature';

      const msg = UPGRADE_MESSAGES[feature];
      toast.message(msg?.title || `${featureName} — upgrade to ${requiredPlan}`, {
        description: msg?.description || `${featureName} is part of your next-tier plan — unlock it and keep your momentum.`,
        action: { label: 'See Plans →', onClick: () => navigate('/pricing') },
        duration: 9000,
      });
    };
    window.addEventListener('upgrade-required', handler);
    return () => window.removeEventListener('upgrade-required', handler);
  }, [navigate]);
  return null;
}

export default App;
App;
